import { useRef, useState, MouseEvent, useEffect } from "react";

type AboutConstantType = {
  key: string;
  title: string;
  content: string;
};

type ClickEventHandler = (
  e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
  index: number
) => void;

export default function Tabs({ tabs }: { tabs: AboutConstantType[] }) {
  const [onResizingMode, setOnResizingMode] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [width, setWidth] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const tabBoxRef = useRef<HTMLDivElement>(null);
  const tabRef = useRef<HTMLDivElement>(null);
  const resizeTimeout = useRef<number | null>(null);

  const handleClick: ClickEventHandler = (e, index) => {
    const element = e.target as HTMLDivElement;
    tabRef.current = element;
    setBarSize(element);
    setTabIndex(index);
  };

  const setBarSize = (element: HTMLDivElement) => {
    const { clientWidth, offsetLeft } = element as HTMLDivElement;
    setWidth(clientWidth);
    setOffset(offsetLeft);
  };

  useEffect(() => {
    if (tabBoxRef.current) {
      const element = tabBoxRef.current.children[1] as HTMLDivElement;
      setBarSize(element);
      const handleResize = () => {
        setOnResizingMode(true);
        if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
        setBarSize(tabRef.current as HTMLDivElement);
        resizeTimeout.current = window.setTimeout(() => {
          setOnResizingMode(false);
        }, 50);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  /**
   * Convierte el string `content` en bloques:
   * - Párrafos para cada línea normal (separadas por \n)
   * - Listas para secuencias de líneas que comienzan con "-"
   */
  const renderContent = (raw: string) => {
    // 1) Normalizamos saltos de línea y quitamos indentaciones extra
    const lines = raw
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    type Block =
      | { type: "p"; text: string }
      | { type: "ul"; items: string[] };

    const blocks: Block[] = [];
    let currentList: string[] = [];

    const flushList = () => {
      if (currentList.length) {
        blocks.push({ type: "ul", items: currentList });
        currentList = [];
      }
    };

    for (const line of lines) {
      if (line.startsWith("-")) {
        // acumulamos ítems de lista (quitamos el guion y espacios)
        const item = line.replace(/^-+\s*/, "");
        currentList.push(item);
      } else {
        // si veníamos en una lista, la cerramos
        flushList();
        // línea normal -> párrafo
        blocks.push({ type: "p", text: line });
      }
    }
    // cerrar lista al final si quedó abierta
    flushList();

    return (
      <div className="space-y-4">
        {blocks.map((b, i) => {
          if (b.type === "p") {
            return (
              <p key={`p-${i}`} className="text-sm sm:text-base text-justify">
                {b.text}
              </p>
            );
          }
          // lista con viñetas
          return (
            <ul
              key={`ul-${i}`}
              className="list-disc pl-6 text-sm sm:text-base text-justify space-y-1"
            >
              {b.items.map((it, j) => (
                <li key={`li-${i}-${j}`}>{it}</li>
              ))}
            </ul>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div
        className="mt-4 sm:mt-6 flex flex-wrap sm:flex-nowrap gap-2 w-full relative"
        ref={tabBoxRef}
      >
        <span
          className={`bg-primary w-10 absolute h-1 transition-transform ${
            onResizingMode ? "duration-0" : "duration-300"
          } hidden sm:block`}
          style={{ width, transform: `translateX(${offset}px)` }}
        />
        {tabs.map((tab, index) => (
          <div
            key={tab.key}
            onClick={(e) => handleClick(e, index)}
            className={`flex items-center justify-center sm:justify-start cursor-pointer grow h-10 sm:h-11 relative transition-colors duration-300 rounded sm:rounded-none ${
              index === tabIndex
                ? "bg-primary/10 sm:bg-transparent border-b-2 sm:border-b-0 border-primary sm:border-primary-0"
                : "bg-[#C5A537]"
            }`}
          >
            <h6 className="text-sm sm:text-base ml-0 sm:ml-2 pointer-events-none">
              {tab.title}
            </h6>
          </div>
        ))}
      </div>

      <div className="mt-4 sm:mt-7 mb-8 sm:mb-16 md:mb-20">
        {renderContent(tabs[tabIndex].content)}
      </div>
    </>
  );
}
